import React, { useRef, useCallback, useMemo, memo } from 'react';
import { Upload } from 'lucide-react';
import { NodeType } from '@/types';
import { useProjectStore } from '@/stores/projectStore';
import { projectApi } from '@/services/projectApi';
import { API_BASE } from '@/config';
import { normalizeAssetUrl } from '@/utils/assetUtils';

interface UploadButtonProps {
  nodeId: string;
  nodeType?: NodeType;
  onUpdate?: (id: string, data: any) => void;
  onUploadStart?: () => void;
  onUploadEnd?: () => void;
}

const UploadButtonInner: React.FC<UploadButtonProps> = ({
  nodeId,
  nodeType,
  onUpdate,
  onUploadStart,
  onUploadEnd,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const acceptTypes = useMemo(() => {
    if (nodeType === NodeType.IMAGE_GENERATOR || nodeType === NodeType.IMAGE_EDITOR) return 'image/*';
    if (nodeType === NodeType.VIDEO_GENERATOR || nodeType === NodeType.VIDEO_ANALYZER || nodeType === NodeType.VIDEO_COMPOSE) return 'video/*';
    if (nodeType === NodeType.AUDIO_GENERATOR) return 'audio/*';
    return 'image/*,video/*,audio/*';
  }, [nodeType]);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const findClosestAspectRatio = (width: number, height: number): string => {
    const actualRatio = width / height;
    const ratios = ['1:1', '3:4', '4:3', '9:16', '16:9', '3:2', '2:3', '5:4', '4:5', '21:9'];
    let closestRatio = '16:9';
    let minDiff = Infinity;
    for (const ratio of ratios) {
      const [w, h] = ratio.split(':').map(Number);
      const ratioValue = w / h;
      const diff = Math.abs(actualRatio - ratioValue);
      if (diff < minDiff) {
        minDiff = diff;
        closestRatio = ratio;
      }
    }
    return closestRatio;
  };

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    onUploadStart?.();

    const { currentProjectId } = useProjectStore.getState();

    const processFile = (result: string) => {
      if (file.type.startsWith('image/')) {
        const img = new Image();
        img.onload = () => {
          const aspectRatio = findClosestAspectRatio(img.width, img.height);
          onUpdate?.(nodeId, {
            image: result,
            videoUri: undefined,
            audioUri: undefined,
            aspectRatio,
            isManualUpload: true,
          });
          onUploadEnd?.();
        };
        img.src = result;
      } else if (file.type.startsWith('video/')) {
        const video = document.createElement('video');
        video.onloadedmetadata = () => {
          const aspectRatio = findClosestAspectRatio(video.videoWidth, video.videoHeight);
          onUpdate?.(nodeId, {
            videoUri: result,
            image: undefined,
            audioUri: undefined,
            aspectRatio,
            isManualUpload: true,
          });
          onUploadEnd?.();
        };
        video.src = result;
      } else if (file.type.startsWith('audio/')) {
        onUpdate?.(nodeId, {
          audioUri: result,
          image: undefined,
          videoUri: undefined,
          isManualUpload: true,
        });
        onUploadEnd?.();
      }
    };

    // 立即用本地 FileReader 显示预览（即时反馈，避免等待后端冷启动）
    // 同时异步上传到后端持久化，上传成功后用后端 URL 替换 data URL
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const localUrl = ev.target?.result as string;
      processFile(localUrl);
      onUploadEnd?.();

      if (!currentProjectId) return;
      try {
        const asset = await projectApi.uploadAsset(currentProjectId, file, {
          source: 'uploaded',
          node_id: nodeId,
          node_type: nodeType,
        });
        const backendUrl = normalizeAssetUrl(asset.url);

        // 预加载后端 URL（绕过 Cloudflare 302 → 后端休眠 pending 的阻塞）
        // 预加载成功后再替换 data URL，保证 workflow 持久化的是后端 URL
        if (file.type.startsWith('image/')) {
          const preload = new Image();
          preload.onload = () => {
            onUpdate?.(nodeId, { image: backendUrl, isManualUpload: true });
          };
          preload.onerror = () => {
            console.warn('[Upload] Backend URL preload failed, keeping local data URL');
          };
          preload.src = backendUrl;
        } else if (file.type.startsWith('video/')) {
          const preload = document.createElement('video');
          preload.onloadeddata = () => {
            onUpdate?.(nodeId, { videoUri: backendUrl, isManualUpload: true });
          };
          preload.onerror = () => {
            console.warn('[Upload] Backend video URL preload failed, keeping local data URL');
          };
          preload.src = backendUrl;
        } else if (file.type.startsWith('audio/')) {
          onUpdate?.(nodeId, { audioUri: backendUrl, isManualUpload: true });
        }
      } catch (err) {
        console.error('[Upload] Backend save failed, keeping local data URL:', err);
      }
    };
    reader.readAsDataURL(file);

    e.target.value = '';
  }, [nodeId, nodeType, onUpdate, onUploadStart, onUploadEnd]);

  return (
    <div className="flex items-center justify-center nodrag">
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptTypes}
        className="hidden"
        onChange={handleFileChange}
      />
      <button
        onClick={handleClick}
        onMouseDown={(e) => e.stopPropagation()}
        className="
          flex items-center gap-1.5
          px-3 py-1.5
          bg-[#1c1c1e]/95
          backdrop-blur-xl
          border border-white/10
          rounded-md
          text-xs font-medium
          text-white/80
          hover:text-white
          hover:bg-white/10
          hover:border-white/20
          transition-all
          shadow-lg
          cursor-pointer
        "
      >
        <Upload size={14} />
        <span>上传</span>
      </button>
    </div>
  );
};

export const UploadButton = memo(UploadButtonInner, (prevProps, nextProps) => {
  return (
    prevProps.nodeId === nextProps.nodeId &&
    prevProps.nodeType === nextProps.nodeType
  );
});
